import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/auth/useAuth';
import CamberLogo from './CamberLogo';
import TriCoinBadge from './TriCoinBadge';

export default function Sidebar() {
  const location = useLocation();
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const onDocDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  const handleLogout = async () => {
    try {
      await logout?.(); // or logout()
    } finally {
      setUserMenuOpen(false);
      navigate("/login"); // adjust to your login route
    }
  };

  const getInitials = (user) => {
    if (user?.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return "U";
  };

  const menuItems = [
    {
      path: '/dashboard',
      iconColor: '#828FC8',
      activeIconColor: '#228BE6',
      activeFrameBg: '#E7F5FF',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 19.9991H9.69231V14.9221C9.69231 14.6933 9.76974 14.5015 9.9245 14.3466C10.0793 14.192 10.2712 14.1146 10.5 14.1146H13.5C13.7288 14.1146 13.9207 14.192 14.0755 14.3466C14.2303 14.5015 14.3077 14.6933 14.3077 14.9221V19.9991H18V11.3069C18 11.2042 17.9776 11.1112 17.9327 11.0279C17.8879 10.9445 17.827 10.8709 17.75 10.8069L12.3655 6.7491C12.2628 6.6595 12.141 6.61462 12 6.61462C11.859 6.61462 11.7372 6.6595 11.6345 6.7491L6.25 10.8069C6.173 10.8709 6.11206 10.9445 6.06725 11.0279C6.02242 11.1112 6 11.2042 6 11.3069V19.9991ZM5 19.9991V11.3069C5 11.051 5.05717 10.8087 5.1715 10.5799C5.28583 10.351 5.44392 10.1626 5.64575 10.0146L11.0308 5.93762C11.3126 5.72229 11.6347 5.61462 11.997 5.61462C12.3593 5.61462 12.6834 5.72229 12.9693 5.93762L18.3538 10.0146C18.5558 10.1626 18.7139 10.351 18.8282 10.5799C18.9427 10.8087 19 11.051 19 11.3069V19.9991C19 20.2671 18.9003 20.5008 18.701 20.7001C18.5017 20.8995 18.268 20.9991 18 20.9991H14.1155C13.8865 20.9991 13.6947 20.9217 13.54 20.7669C13.3852 20.612 13.3077 20.4202 13.3077 20.1914V15.1146H10.6923V20.1914C10.6923 20.4202 10.6148 20.612 10.46 20.7669C10.3053 20.9217 10.1135 20.9991 9.8845 20.9991H6C5.732 20.9991 5.49833 20.8995 5.299 20.7001C5.09967 20.5008 5 20.2671 5 19.9991Z" fill="currentColor" />
        </svg>
      ),
      label: 'Dashboard',
    },
    {
      path: '/videos',
      iconColor: '#8996CC',
      activeIconColor: '#40C057',
      activeFrameBg: '#EBFBEE',
      icon: (
        <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <mask id="mask0_video" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="4" y="4" width="24" height="24">
            <rect x="4" y="4" width="24" height="24" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_video)">
            <path d="M9.09597 23C8.63564 23 8.2513 22.8458 7.94297 22.5375C7.63464 22.2292 7.48047 21.8448 7.48047 21.3845V10.6155C7.48047 10.1552 7.63464 9.77083 7.94297 9.4625C8.2513 9.15417 8.63564 9 9.09597 9H19.8652C20.3254 9 20.7096 9.15417 21.018 9.4625C21.3263 9.77083 21.4805 10.1552 21.4805 10.6155V15.2692L23.8227 12.927C23.9511 12.7987 24.0991 12.7666 24.267 12.8307C24.435 12.8949 24.519 13.018 24.519 13.2V18.8C24.519 18.982 24.435 19.1051 24.267 19.1692C24.0991 19.2334 23.9511 19.2013 23.8227 19.073L21.4805 16.7308V21.3845C21.4805 21.8448 21.3263 22.2292 21.018 22.5375C20.7096 22.8458 20.3254 23 19.8652 23H9.09597ZM9.09597 22H19.8652C20.0446 22 20.192 21.9423 20.3075 21.827C20.4228 21.7115 20.4805 21.564 20.4805 21.3845V10.6155C20.4805 10.436 20.4228 10.2885 20.3075 10.173C20.192 10.0577 20.0446 10 19.8652 10H9.09597C8.91647 10 8.76897 10.0577 8.65347 10.173C8.53814 10.2885 8.48047 10.436 8.48047 10.6155V21.3845C8.48047 21.564 8.53814 21.7115 8.65347 21.827C8.76897 21.9423 8.91647 22 9.09597 22Z" fill="currentColor" />
          </g>
        </svg>
      ),
      label: 'Videos',
    },
    {
      path: '/routes',
      iconColor: '#828FC8',
      activeIconColor: '#7950F2',
      activeFrameBg: '#F3F0FF',
      icon: (
        <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <mask id="mask0_routes" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="4" y="4" width="24" height="24">
            <rect x="4" y="4" width="24" height="24" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_routes)">
            <path d="M13 24.5C12.0282 24.5 11.2019 24.1596 10.5213 23.4788C9.84042 22.7981 9.5 21.9718 9.5 21V12.4788C8.91667 12.2621 8.4375 11.9317 8.0625 11.4875C7.6875 11.0433 7.5 10.5497 7.5 10.0065C7.5 9.31033 7.74283 8.7185 8.2285 8.231C8.71417 7.74367 9.30392 7.5 9.99775 7.5C10.6916 7.5 11.2821 7.74367 11.7692 8.231C12.2564 8.7185 12.5 9.31033 12.5 10.0065C12.5 10.5497 12.3125 11.0433 11.9375 11.4875C11.5625 11.9317 11.0833 12.2621 10.5 12.4788V21C10.5 21.6875 10.7452 22.276 11.2355 22.7655C11.7258 23.2552 12.3152 23.5 13.0037 23.5C13.6922 23.5 14.2804 23.2552 14.7683 22.7655C15.2561 22.276 15.5 21.6875 15.5 21V11C15.5 10.0282 15.8404 9.20192 16.5213 8.52125C17.2019 7.84042 18.0282 7.5 19 7.5C19.9718 7.5 20.7981 7.84042 21.4788 8.52125C22.1596 9.20192 22.5 10.0282 22.5 11V19.5213C23.0833 19.7379 23.5625 20.0683 23.9375 20.5125C24.3125 20.9567 24.5 21.4503 24.5 21.9935C24.5 22.6897 24.2572 23.2815 23.7715 23.769C23.2858 24.2563 22.6961 24.5 22.0023 24.5C21.3084 24.5 20.7179 24.2563 20.2308 23.769C19.7436 23.2815 19.5 22.6897 19.5 21.9935C19.5 21.4503 19.6875 20.9525 20.0625 20.5C20.4375 20.0475 20.9167 19.7213 21.5 19.5213V11C21.5 10.3125 21.2548 9.724 20.7645 9.2345C20.2742 8.74483 19.6847 8.5 18.9962 8.5C18.3077 8.5 17.7196 8.74483 17.2318 9.2345C16.7439 9.724 16.5 10.3125 16.5 11V21C16.5 21.9718 16.1596 22.7981 15.4787 23.4788C14.7981 24.1596 13.9718 24.5 13 24.5ZM10 11.5C10.4115 11.5 10.7644 11.3529 11.0588 11.0587C11.3529 10.7644 11.5 10.4115 11.5 10C11.5 9.5885 11.3529 9.23558 11.0588 8.94125C10.7644 8.64708 10.4115 8.5 10 8.5C9.5885 8.5 9.23558 8.64708 8.94125 8.94125C8.64708 9.23558 8.5 9.5885 8.5 10C8.5 10.4115 8.64708 10.7644 8.94125 11.0587C9.23558 11.3529 9.5885 11.5 10 11.5ZM22 23.5C22.4115 23.5 22.7644 23.3529 23.0587 23.0587C23.3529 22.7644 23.5 22.4115 23.5 22C23.5 21.5885 23.3529 21.2356 23.0587 20.9413C22.7644 20.6471 22.4115 20.5 22 20.5C21.5885 20.5 21.2356 20.6471 20.9413 20.9413C20.6471 21.2356 20.5 21.5885 20.5 22C20.5 22.4115 20.6471 22.7644 20.9413 23.0587C21.2356 23.3529 21.5885 23.5 22 23.5Z" fill="currentColor" />
          </g>
        </svg>
      ),
      label: 'Routes',
    },
    {
      path: '/routes/map',
      iconColor: '#828FC8',
      activeIconColor: '#EB6CCF',
      activeFrameBg: '#FFF0F6',
      icon: (
        <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <mask id="mask0_routes_map" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="4" y="4" width="24" height="24">
            <rect x="4" y="4" width="24" height="24" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_routes_map)">
            <path d="M18.4845 23.7396L13 21.8261L9.09825 23.3346C8.95842 23.3906 8.82458 23.4085 8.69675 23.3881C8.56875 23.3678 8.45092 23.3238 8.34325 23.2561C8.23558 23.1883 8.15142 23.0969 8.09075 22.9819C8.03025 22.8667 8 22.7347 8 22.5859V10.4134C8 10.2254 8.04325 10.059 8.12975 9.91438C8.21625 9.76971 8.34225 9.66854 8.50775 9.61088L12.4655 8.26663C12.5477 8.23329 12.6333 8.20829 12.7225 8.19163C12.8115 8.17496 12.904 8.16663 13 8.16663C13.096 8.16663 13.1885 8.17496 13.2775 8.19163C13.3667 8.20829 13.4523 8.23329 13.5345 8.26663L19 10.1801L22.9018 8.67163C23.0416 8.61563 23.1754 8.59779 23.3033 8.61813C23.4313 8.63846 23.5491 8.68246 23.6568 8.75013C23.7644 8.81796 23.8486 8.90938 23.9093 9.02438C23.9698 9.13954 24 9.27154 24 9.42038V21.6786C24 21.868 23.9471 22.0332 23.8413 22.1744C23.7356 22.3154 23.5937 22.4147 23.4155 22.4724L19.5155 23.7589C19.4282 23.7922 19.3427 23.814 19.2592 23.8244C19.1756 23.8345 19.0887 23.8396 18.9985 23.8396C18.9085 23.8396 18.8222 23.8313 18.7395 23.8146C18.6568 23.798 18.5718 23.773 18.4845 23.7396ZM18.5 22.7069V11.0069L13.5 9.26088V20.9609L18.5 22.7069ZM19.5 22.7069L23 21.5531V9.70313L19.5 11.0069V22.7069ZM9 22.3031L12.5 20.9609V9.26088L9 10.4531V22.3031Z" fill="currentColor" />
          </g>
        </svg>
      ),
      label: 'Map & Heatmap',
    },
    {
      path: '/reports',
      iconColor: '#828FC8',
      activeIconColor: '#7950F2',
      activeFrameBg: '#F3F0FF',
      icon: (
        <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <mask id="mask0_reports" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="4" y="4" width="24" height="24">
            <rect x="4" y="4" width="24" height="24" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_reports)">
            <path d="M9.6155 24C9.17117 24 8.79083 23.8418 8.4745 23.5255C8.15817 23.2092 8 22.8288 8 22.3845V9.6155C8 9.17117 8.15817 8.79083 8.4745 8.4745C8.79083 8.15817 9.17117 8 9.6155 8H14.3155C14.2243 7.48967 14.3464 7.02875 14.6817 6.61725C15.0169 6.20575 15.4595 6 16.0095 6C16.5595 6 17.0022 6.20575 17.3375 6.61725C17.6728 7.02875 17.7885 7.48967 17.6845 8H22.3845C22.8288 8 23.2092 8.15817 23.5255 8.4745C23.8418 8.79083 24 9.17117 24 9.6155V22.3845C24 22.8288 23.8418 23.2092 23.5255 23.5255C23.2092 23.8418 22.8288 24 22.3845 24H9.6155ZM9.6155 23H22.3845C22.5385 23 22.6796 22.9359 22.8077 22.8078C22.9359 22.6796 23 22.5385 23 22.3845V9.6155C23 9.4615 22.9359 9.32042 22.8077 9.19225C22.6796 9.06408 22.5385 9 22.3845 9H9.6155C9.4615 9 9.32042 9.06408 9.19225 9.19225C9.06408 9.32042 9 9.4615 9 9.6155V22.3845C9 22.5385 9.06408 22.6796 9.19225 22.8078C9.32042 22.9359 9.4615 23 9.6155 23ZM12 20.2693H17C17.1417 20.2693 17.2604 20.2213 17.3562 20.1253C17.4521 20.0293 17.5 19.9103 17.5 19.7685C17.5 19.6267 17.4521 19.508 17.3562 19.4125C17.2604 19.317 17.1417 19.2693 17 19.2693H12C11.8583 19.2693 11.7396 19.3173 11.6437 19.4133C11.5479 19.5091 11.5 19.628 11.5 19.77C11.5 19.9118 11.5479 20.0305 11.6437 20.126C11.7396 20.2215 11.8583 20.2693 12 20.2693ZM12 16.5H20C20.1417 16.5 20.2604 16.452 20.3562 16.356C20.4521 16.26 20.5 16.1411 20.5 15.9993C20.5 15.8574 20.4521 15.7388 20.3562 15.6433C20.2604 15.5478 20.1417 15.5 20 15.5H12C11.8583 15.5 11.7396 15.548 11.6437 15.644C11.5479 15.74 11.5 15.8589 11.5 16.0008C11.5 16.1426 11.5479 16.2612 11.6437 16.3567C11.7396 16.4522 11.8583 16.5 12 16.5ZM12 12.7307H20C20.1417 12.7307 20.2604 12.6827 20.3562 12.5867C20.4521 12.4909 20.5 12.372 20.5 12.23C20.5 12.0882 20.4521 11.9695 20.3562 11.874C20.2604 11.7785 20.1417 11.7307 20 11.7307H12C11.8583 11.7307 11.7396 11.7787 11.6437 11.8747C11.5479 11.9707 11.5 12.0897 11.5 12.2315C11.5 12.3733 11.5479 12.492 11.6437 12.5875C11.7396 12.683 11.8583 12.7307 12 12.7307ZM16 8.44225C16.2167 8.44225 16.3958 8.37142 16.5375 8.22975C16.6792 8.08808 16.75 7.90892 16.75 7.69225C16.75 7.47558 16.6792 7.29642 16.5375 7.15475C16.3958 7.01308 16.2167 6.94225 16 6.94225C15.7833 6.94225 15.6042 7.01308 15.4625 7.15475C15.3208 7.29642 15.25 7.47558 15.25 7.69225C15.25 7.90892 15.3208 8.08808 15.4625 8.22975C15.6042 8.37142 15.7833 8.44225 16 8.44225Z" fill="currentColor" />
          </g>
        </svg>
      ),
      label: 'Reports',
    },
  ];

  // Only show users menu if user is admin
  if (user?.is_staff) {
    menuItems.push({
      path: '/users',
      iconColor: '#828FC8',
      activeIconColor: '#40C057',
      activeFrameBg: '#EBFBEE',
      icon: (
        <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <mask id="mask0_users" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="4" y="4" width="24" height="24">
            <rect x="4" y="4" width="24" height="24" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_users)">
            <path d="M6.59619 20.9693C6.59619 20.5051 6.71636 20.106 6.95669 19.772C7.19702 19.4382 7.52002 19.1714 7.92569 18.9715C8.79319 18.5585 9.65444 18.2293 10.5094 17.9838C11.3646 17.7383 12.3935 17.6155 13.5962 17.6155C14.7987 17.6155 15.8275 17.7383 16.6827 17.9838C17.5379 18.2293 18.3991 18.5585 19.2664 18.9715C19.6721 19.1714 19.9951 19.4382 20.2354 19.772C20.4759 20.106 20.5962 20.5051 20.5962 20.9693V21.577C20.5962 21.832 20.4964 22.0689 20.2969 22.2875C20.0976 22.5062 19.8512 22.6155 19.5577 22.6155H7.63444C7.34094 22.6155 7.09452 22.5158 6.89519 22.3163C6.69586 22.1169 6.59619 21.8705 6.59619 21.577V20.9693ZM24.3654 22.6155H22.3729C22.4409 22.4565 22.4951 22.2889 22.5354 22.1125C22.5759 21.9362 22.5962 21.7577 22.5962 21.577V20.923C22.5962 20.3435 22.4789 19.7965 22.2442 19.282C22.0097 18.7675 21.677 18.3261 21.2462 17.9578C21.7372 18.0578 22.2103 18.1965 22.6654 18.374C23.1204 18.5517 23.5704 18.7514 24.0154 18.973C24.4488 19.191 24.7885 19.4699 25.0347 19.8098C25.2807 20.1498 25.4037 20.5209 25.4037 20.923V21.577C25.4037 21.8705 25.304 22.1169 25.1047 22.3163C24.9054 22.5158 24.6589 22.6155 24.3654 22.6155ZM13.5962 15.3845C12.7712 15.3845 12.0649 15.0908 11.4774 14.5033C10.8899 13.9158 10.5962 13.2095 10.5962 12.3845C10.5962 11.5595 10.8899 10.8533 11.4774 10.2658C12.0649 9.67827 12.7712 9.38452 13.5962 9.38452C14.4212 9.38452 15.1274 9.67827 15.7149 10.2658C16.3024 10.8533 16.5962 11.5595 16.5962 12.3845C16.5962 13.2095 16.3024 13.9158 15.7149 14.5033C15.1274 15.0908 14.4212 15.3845 13.5962 15.3845ZM20.8654 12.3845C20.8654 13.2095 20.5717 13.9158 19.9842 14.5033C19.3967 15.0908 18.6904 15.3845 17.8654 15.3845C17.8231 15.3845 17.7692 15.3798 17.7037 15.3703C17.6384 15.3606 17.5845 15.35 17.5422 15.3385C17.881 14.9235 18.1414 14.4632 18.3232 13.9575C18.5052 13.4519 18.5962 12.9268 18.5962 12.3823C18.5962 11.8378 18.5009 11.3174 18.3104 10.8213C18.1201 10.3251 17.864 9.8616 17.5422 9.43077C17.596 9.4116 17.6499 9.3991 17.7037 9.39327C17.7575 9.38744 17.8114 9.38452 17.8654 9.38452C18.6904 9.38452 19.3967 9.67827 19.9842 10.2658C20.5717 10.8533 20.8654 11.5595 20.8654 12.3845ZM7.59619 21.6155H19.5962V20.9693C19.5962 20.7346 19.5375 20.5294 19.4202 20.3538C19.3029 20.1783 19.0923 20.0104 18.7884 19.85C18.0423 19.4514 17.2557 19.1459 16.4287 18.9338C15.6019 18.7216 14.6577 18.6155 13.5962 18.6155C12.5345 18.6155 11.5903 18.7216 10.7634 18.9338C9.93644 19.1459 9.14986 19.4514 8.40369 19.85C8.09986 20.0104 7.88927 20.1783 7.77194 20.3538C7.65477 20.5294 7.59619 20.7346 7.59619 20.9693V21.6155ZM13.5962 14.3845C14.1462 14.3845 14.617 14.1887 15.0087 13.797C15.4004 13.4054 15.5962 12.9345 15.5962 12.3845C15.5962 11.8345 15.4004 11.3637 15.0087 10.972C14.617 10.5804 14.1462 10.3845 13.5962 10.3845C13.0462 10.3845 12.5754 10.5804 12.1837 10.972C11.792 11.3637 11.5962 11.8345 11.5962 12.3845C11.5962 12.9345 11.792 13.4054 12.1837 13.797C12.5754 14.1887 13.0462 14.3845 13.5962 14.3845Z" fill="currentColor" />
          </g>
        </svg>
      ),
      label: 'Users',
    });
  }

  // Highlight only ONE item: the most specific matching path.
  // Example: when on `/routes/map`, highlight only that (not also `/routes`).
  const normalizedPathname =
    location.pathname === '/' ? '/dashboard' : location.pathname.replace(/\/+$/, '');

  let activeMenuPath = null;
  let bestMatchLength = -1;
  for (const item of menuItems) {
    const itemPath = item.path;
    const isMatch =
      normalizedPathname === itemPath || normalizedPathname.startsWith(`${itemPath}/`);

    if (isMatch && itemPath.length > bestMatchLength) {
      bestMatchLength = itemPath.length;
      activeMenuPath = itemPath;
    }
  }

  return (
    <div className="fixed left-0 top-0 h-full w-[66px] bg-white border-r border-[#EAEAEA] z-50">
      {/* Header - Desktop */}
      <div className="w-[66px] h-[57.91px] bg-white flex flex-row justify-center items-center px-0 gap-[10px]">
        <CamberLogo width={41} height={32} className="w-[41px] h-[32px]" />
      </div>

      {/* Navbar */}
      <div className="w-[66px] h-[calc(100%-57.91px)] bg-white flex flex-col justify-between items-start pt-[120px] px-[10px] pb-[10px]">
        {/* Frame 2 */}
        <div className="mx-auto w-[46px]">
          <ul className="flex flex-col items-start w-[46px]">
            {menuItems.map((item) => {
              const active = activeMenuPath === item.path;
              const baseIconColor = item.iconColor || '#828FC8';
              const iconColor = active ? (item.activeIconColor || baseIconColor) : baseIconColor;

              const iconFrameBg = active ? (item.activeFrameBg || 'transparent') : 'transparent';
              const iconEl = React.isValidElement(item.icon)
                ? React.cloneElement(item.icon, { width: 32, height: 32 })
                : item.icon;

              return (
                <li key={item.path} className="w-[46px] h-[46px]">
                  <Link
                    to={item.path}
                    className="w-[46px] h-[46px] rounded-[4px] flex flex-row justify-center items-center p-[10px] gap-[20px]"
                    style={{ background: 'white' }}
                    aria-label={item.label}
                    data-active={active ? 'true' : 'false'}
                  >
                    <span
                      className="w-[42px] h-[42px] rounded-[4px] flex flex-col justify-center items-center p-0"
                      style={{ background: iconFrameBg }}
                    >
                      <span
                        className="w-[42px] h-[42px] flex items-center justify-center"
                        style={{ color: iconColor }}
                      >
                        {iconEl}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* TriCoin Balance Badge */}
        {/*<div className="mx-auto w-[46px] flex flex-col items-center gap-[10px] py-2">
          <div className="flex justify-center">
            <TriCoinBadge className="transform scale-75" />
          </div>
        </div>
        */}

        {/* Frame 12 (Divider + Avatar) */}
        <div className="mx-auto w-[46px] flex flex-col items-start gap-[10px]" ref={userMenuRef}>
          <div className="w-[46px] border-t border-[#E9ECEF]" />

          <div className="relative w-[46px]">
            {/* Avatar button */}
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              className="w-[46px] h-[46px] p-0 rounded-full flex items-center justify-center
                        hover:bg-[#F4F7FA] transition-colors"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
            >
              <div
                className="w-[38px] h-[38px] rounded-full overflow-hidden flex items-center justify-center shrink-0"
                style={{ background: "linear-gradient(138.22deg, #FF5197 17.28%, #4B0CC0 74.45%)" }}
              >
                <span className="text-[14px] leading-none font-normal text-white">
                  {getInitials(user)}
                </span>
              </div>
            </button>

            {/* Dropdown */}
            {userMenuOpen && (
              <div
                className="absolute bottom-[0px] left-[56px] w-[160px] bg-white border border-[#E0E2E5] rounded-[8px] shadow-[0px_2px_8px_rgba(0,0,0,0.12)] overflow-hidden z-50"
                role="menu"
              >
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/profile"); // if you have it
                  }}
                  className="w-full h-[40px] px-[12px] flex items-center text-[14px] text-[#333333] hover:bg-[#F4F7FA]"
                  role="menuitem"
                >
                  My profile
                </button>

                <div className="w-full h-[1px] bg-[#E9ECEF]" />

                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full h-[40px] px-[12px] flex items-center text-[14px] text-[#C92A2A] hover:bg-[#F4F7FA]"
                  role="menuitem"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
